/**
 * A Pressable with the desktop hover language built in: the drop-in for
 * TouchableOpacity at every migrated site.
 *
 *   <Hoverable hover="row" onPress={...} style={styles.row}>…</Hoverable>
 *
 * Hover (web only) comes from useHoverStyle(kind). Pressed feedback is
 * `opacity: pressedOpacity` (0.7 by default) on every platform, replacing
 * TouchableOpacity's 0.2 flash; pass 1 for none. `hoverStyle` adds a bespoke
 * hovered style; function children receive `{ hovered, pressed, textStyle }`
 * so a `link` can put the hovered ink on the Text it owns. `bleed` (row only)
 * moves the tint onto a rounded HighlightPlate behind the children that
 * reaches past (or stays inside) this element's box.
 */
import React, { forwardRef, useCallback } from 'react';
import { Pressable, View } from 'react-native';
import type { PressableProps, PressableStateCallbackType, StyleProp, ViewStyle } from 'react-native';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import type { HoverKind } from '@/hooks/shared/useHoverStyle';
import { HighlightPlate } from './HighlightPlate';
import type { PlateBleed } from './HighlightPlate';

type HoverEvent = Parameters<NonNullable<PressableProps['onHoverIn']>>[0];

export interface HoverableRenderState {
  hovered: boolean;
  pressed: boolean;
  /** `link` only: the hovered ink for the Text; null for other kinds. */
  textStyle: any[] | null;
}

export interface HoverableProps extends Omit<PressableProps, 'style' | 'children'> {
  /** Which hover treatment to apply. Default `row`. */
  hover?: HoverKind;
  /**
   * `row` only: paint the hover tint as a rounded HighlightPlate behind the
   * children instead of on this element, reaching `bleed` past its box:
   * positive outward when the content touches the edges, negative inward when
   * the row spans its card or menu. A number is left and right; `{ x, y }`.
   */
  bleed?: PlateBleed;
  /** Extra style while hovered, for the rare bespoke case. */
  hoverStyle?: StyleProp<ViewStyle>;
  /** Opacity while pressed. Default 0.7; 1 adds no pressed style. */
  pressedOpacity?: number;
  /** Lift in px for `card` and `button`. Default 1. */
  liftPx?: number;
  style?: StyleProp<ViewStyle> | ((state: { pressed: boolean; hovered: boolean }) => StyleProp<ViewStyle>);
  children?: React.ReactNode | ((state: HoverableRenderState) => React.ReactNode);
}

export const Hoverable = forwardRef<View, HoverableProps>(function Hoverable(
  { hover = 'row', bleed, hoverStyle, pressedOpacity = 0.7, liftPx, style, children, disabled, onHoverIn, onHoverOut, ...rest },
  ref,
) {
  // A plated row keeps the cursor and hover tracking ('none' paints nothing)
  // and draws its tint on the plate instead; every other case is the kind as given.
  const plated = hover === 'row' && bleed !== undefined;
  const { isHovered, hoverProps, viewStyle, textStyle } = useHoverStyle(plated ? 'none' : hover, { disabled: !!disabled, liftPx });

  const handleHoverIn = useCallback(
    (e: HoverEvent) => {
      hoverProps.onHoverIn?.();
      onHoverIn?.(e);
    },
    [hoverProps, onHoverIn],
  );
  const handleHoverOut = useCallback(
    (e: HoverEvent) => {
      hoverProps.onHoverOut?.();
      onHoverOut?.(e);
    },
    [hoverProps, onHoverOut],
  );
  const hasHover = hoverProps.onHoverIn != null;

  const resolveStyle = useCallback(
    ({ pressed }: PressableStateCallbackType) => [
      typeof style === 'function' ? style({ pressed, hovered: isHovered }) : style,
      viewStyle,
      isHovered ? hoverStyle : null,
      pressed && pressedOpacity < 1 ? { opacity: pressedOpacity } : null,
    ],
    [style, viewStyle, isHovered, hoverStyle, pressedOpacity],
  );

  // The plate goes first so it paints under the children; without one the
  // children pass through untouched.
  const withPlate = (node: React.ReactNode) =>
    plated ? (
      <>
        <HighlightPlate visible={isHovered} bleed={bleed} />
        {node}
      </>
    ) : (
      node
    );

  return (
    <Pressable
      ref={ref}
      disabled={disabled}
      {...rest}
      onHoverIn={hasHover ? handleHoverIn : onHoverIn}
      onHoverOut={hasHover ? handleHoverOut : onHoverOut}
      style={resolveStyle}
    >
      {typeof children === 'function'
        ? ({ pressed }: PressableStateCallbackType) =>
            withPlate((children as (s: HoverableRenderState) => React.ReactNode)({ hovered: isHovered, pressed, textStyle }))
        : withPlate(children)}
    </Pressable>
  );
});
